import type { Unsubscribe } from '@amazon/vinyl-util'
import {
    createLogPrefix,
    defaultMergeOptions,
    equalDeep,
    logError,
    type LogTarget,
    logWarn,
    LruCache,
    type Maybe,
    mergeDeep,
    type MergeOptions,
    type MergeRule,
    signal,
    type Timestamp,
} from '@amazon/vinyl-util'
import {
    data,
    externalData,
    isObservableValue,
    type ObservableValue,
} from '@amazon/vinyl-observable'
import { type DBSchema, type IDBPDatabase, openDB } from 'idb'

const DB_NAME = 'entityCache'
const STORE = 'optimisticUpdates'
const DB_VERSION = 1
/** Index over each overlay's `expiresAt` — bounds the prune to the stale tail. */
const EXPIRES_INDEX = 'expiresAt'

const target: LogTarget = { logPrefix: createLogPrefix('normalizedCache') }

/**
 * An entity is cacheable if it has both __typename and id.
 * Properties typed as optional to avoid conversion from gql types. When values are missing a
 * warning will be logged and data will not be live/cached.
 */
export type CacheableEntity = {
    readonly __typename?: string
    readonly id?: string | null
}

// Entity merge overrides the default array-CONCAT rule with array-REPLACE: a
// fresh write's array field (e.g. `images`, `edges`) wins wholesale over the
// cached one. Concatenation is wrong for a normalized entity cache — re-saving
// an entity on every gqlFetch would grow its arrays without bound (a memory
// leak) and hand back a corrupted, duplicate-laden response. Replace also keeps
// `equalDeep` meaningful so a re-save of unchanged data short-circuits to the
// same cached instance (the flyweight guarantee).
const arrayReplaceRule: MergeRule<unknown[]> = {
    matches: (object): object is unknown[] => Array.isArray(object),
    merge: (_sourceA, sourceB, merge) => sourceB.map((element, i) => merge(undefined, element, i)),
}

const entityMergeOptions: MergeOptions = {
    ...defaultMergeOptions,
    // Array rule first so it shadows defaultMergeOptions' concat rule.
    rules: [arrayReplaceRule, ...defaultMergeOptions.rules],
}

export function entityCacheKey(entity: Maybe<CacheableEntity>): string | null {
    if (!entity || !entity.__typename || !entity.id) {
        logWarn(target, 'entity missing __typename or id', entity)
        return null
    }
    return `${entity.__typename}_${entity.id}`
}

export function cacheKey(typename: string, id: string): string | null {
    if (!typename || !id) return null
    return `${typename}_${id}`
}

type CachedEntityChangeEvent = {
    readonly key: string
    readonly previous: CacheableEntity | null
    readonly current: CacheableEntity
}

// Signals that the entity with the cache key has changed
const cachedEntityChange = signal<CachedEntityChangeEvent>()

const entitiesCached = new LruCache<string, CacheableEntity>(/* initial capacity */ 2000)

/**
 * The overlaid partial + its expiry for a key with a live optimistic update.
 * The in-memory twin of the persisted idb record: it's what lets `updateEntity`
 * keep a server write from clobbering an in-flight optimistic value.
 */
interface ActiveOverlay {
    readonly entity: CacheableEntity
    readonly expiresAt: Timestamp
}

/**
 * Overlay-authoritative-within-TTL registry. While a key has a non-expired
 * entry here, its overlaid fields WIN over server-sourced `updateEntity` writes
 * — a persisted unlike can't be re-liked by a stale server echo (the prime, an
 * SWR revalidation, a detail-page refetch) that lands after the overlay, no
 * matter which order the writes race in. An entry is cleared only when its TTL
 * lapses or a fresh `optimisticUpdate` supersedes it. Server data flows normally
 * once the overlay expires.
 */
const activeOverlays = new Map<string, ActiveOverlay>()

/**
 * An observable of the cached entity that re-emits whenever any writer updates
 * that `type`+`id`, so every view of the same entity stays in sync. Primes the
 * cache with `entity` and re-applies any persisted optimistic overlay before
 * subscribing; an uncacheable entity (missing `__typename`/`id`) falls back to a
 * static observable and is never written to the cache.
 */
export function liveEntity<T extends CacheableEntity>(entity: T): ObservableValue<T>
export function liveEntity<T extends CacheableEntity>(
    typename: T['__typename'],
    id: string
): ObservableValue<T | null>

export function liveEntity<T extends CacheableEntity>(entityOrTypename: T | string, id?: string) {
    let entity: CacheableEntity
    let key: string | null
    if (typeof entityOrTypename === 'string') {
        entity = { __typename: entityOrTypename, id: id! }
        key = cacheKey(entityOrTypename, id!)
        if (!key) return data(null)
    } else {
        entity = entityOrTypename
        key = entityCacheKey(entity)
        if (!key) return data(entity) // missing fields, cannot be used as a live entity
    }

    const merged = updateEntity(entity)
    checkOptimisticUpdate(key) // if there is a local overlay persisted, use it
    return externalData(merged, (setData) => {
        return cachedEntityChange.listen((event) => {
            if (key !== event.key) return
            setData(event.current as T)
        })
    })
}

export function updateEntity<T extends CacheableEntity>(entity: T): T
export function updateEntity<T extends CacheableEntity>(entity: Maybe<T>): T | null

/**
 * Merge `entity` into its cached `type`+`id` instance and return the canonical
 * merged object (null when uncacheable). A deep-equal re-save returns the same
 * instance (the flyweight) and skips the change dispatch.
 */
export function updateEntity(entity: Maybe<CacheableEntity>): CacheableEntity | null {
    if (!entity) return null
    const key = entityCacheKey(entity)
    if (!key) return null
    const previous = entitiesCached.get(key) ?? null
    let merged = mergeDeep([previous, entity], entityMergeOptions)
    // Overlay-authoritative-within-TTL: a live optimistic overlay's fields win
    // over this write regardless of source or arrival order, so a stale server
    // echo (the load-time prime, an SWR revalidation) can't undo a persisted
    // optimistic value before its TTL. `optimisticUpdate`/`checkOptimisticUpdate`
    // register the overlay first, so re-applying it here is idempotent for the
    // overlay's own writes and only overrides competing server writes.
    const overlay = getActiveOverlay(key)
    if (overlay) merged = mergeDeep([merged, overlay.entity], entityMergeOptions)
    if (equalDeep(previous, merged)) return previous
    entitiesCached.set(key, merged)
    cachedEntityChange.dispatch({
        key,
        previous,
        current: merged,
    })
    return merged
}

export function asLiveEntity<T extends CacheableEntity>(
    entity: ObservableValue<Maybe<T>> | T
): ObservableValue<Maybe<T>> {
    if (isObservableValue(entity)) {
        return externalData(entity.value, (setData) => {
            let previousKey: string | null = null
            let previousSub: Unsubscribe | null = null
            // Skip the key lookup for a legit-null source (e.g. FullPlayer's
            // no-track state) so entityCacheKey doesn't log-spam on it.
            const sourceSub = entity.onData((providedValue) => {
                const key = providedValue == null ? null : entityCacheKey(providedValue)
                // Prime only when this entity is absent: a stale remount must
                // not clobber a fresher cached value once its overlay TTL lapses.
                if (key && readEntity(providedValue!.__typename!, providedValue!.id!) == null) {
                    updateEntity(providedValue)
                }
                if (key !== previousKey) {
                    previousSub?.()
                    // Subscribe via the typename+id overload (which writes only a
                    // bare {type,id} stub), NOT the entity overload — the latter
                    // re-primes `providedValue` and would undo the guard above.
                    previousSub = key
                        ? liveEntity<T>(
                              providedValue!.__typename as T['__typename'],
                              providedValue!.id!
                          ).onData(setData)
                        : null
                    if (!key) setData(null)
                    previousKey = key
                }
            })
            return () => {
                sourceSub()
                previousSub?.()
            }
        })
    } else {
        return liveEntity(entity)
    }
}

/**
 * Read the cached entity for a `__typename` + `id`, or null when nothing is
 * cached. A pure read — unlike `liveEntity`, it never writes a bare `{type,id}`
 * stub into the cache, so a lookup for an absent entity leaves the cache
 * untouched.
 */
export function readEntity(typename: string, id: string): CacheableEntity | null {
    const key = cacheKey(typename, id)
    if (!key) return null
    return entitiesCached.get(key) ?? null
}

/**
 * Empty the normalized entity store. A soft remount (e.g. on locale change)
 * clears the SWR response cache but otherwise survives in memory, so a fresh
 * query's scalars can `mergeDeep` over entities cached under the previous
 * locale. Call this alongside that response-cache clear so no stale-locale
 * entity fields outlive the switch.
 */
export function clearNormalizedCache(): void {
    entitiesCached.clear()
}

/**
 * Walk a gql response and, for every {@link CacheableEntity} in it (an object
 * with both `__typename` and `id`), save it via {@link updateEntity} and swap
 * the response's copy for the canonical cached instance it returns. So the
 * response holds one shared reference per entity — repeated occurrences of the
 * same `type`+`id` collapse to a single object (flyweight), and the app never
 * keeps a duplicate of an entity it has already cached. Builds a new object
 * graph rather than mutating the input, so a `ReadonlyDeep` response is safe to
 * pass. Post-order: children are flyweighted before their parent is saved.
 */
export function saveResponseEntities<T>(value: T): T {
    return saveResponseEntitiesInner(value) as T
}

function saveResponseEntitiesInner(value: unknown): unknown {
    if (value == null || typeof value !== 'object') return value
    if (Array.isArray(value)) return value.map(saveResponseEntitiesInner)
    const source = value as Record<string, unknown>
    const rebuilt: Record<string, unknown> = {}
    for (const key in source) rebuilt[key] = saveResponseEntitiesInner(source[key])
    if (typeof rebuilt.__typename === 'string' && typeof rebuilt.id === 'string') {
        return updateEntity(rebuilt)
    }
    return rebuilt
}

/** The non-expired active overlay for `key`, lazily dropping it once its TTL lapses. */
function getActiveOverlay(key: string): ActiveOverlay | null {
    const overlay = activeOverlays.get(key)
    if (!overlay) return null
    if (Date.now() >= overlay.expiresAt) {
        activeOverlays.delete(key)
        return null
    }
    return overlay
}

/** Register `entity` as the live overlay for `key` until `expiresAt`; its fields then win in `updateEntity`. */
function registerActiveOverlay(key: string, entity: CacheableEntity, expiresAt: Timestamp): void {
    activeOverlays.set(key, { entity, expiresAt })
}

export type OptimisticUpdateOptions = {
    /**
     * The number of seconds to accept an optimistic update over server data.
     */
    readonly time?: number

    /**
     * Persist across browser reloads.
     */
    readonly persist?: boolean
}

/** Hard cap (10 min) on how long an optimistic overlay may mask server data; see `optimisticExpiresAt`. */
export const MAX_OPTIMISTIC_UPDATE_TIME_SECONDS = 600

export const optimisticUpdateDefaults = {
    time: 120,
    persist: true,
} as const satisfies Required<OptimisticUpdateOptions>

/**
 * One persisted optimistic overlay. `expiresAt` is the absolute epoch-ms
 * deadline — already clamped to MAX_OPTIMISTIC_UPDATE_TIME_SECONDS on write — and
 * is the single field that drives write, read (staleness gate), and prune (indexed
 * cursor bound), so those three paths can never disagree on when an overlay dies.
 */
interface PersistedOptimisticUpdate {
    readonly key: string
    readonly entity: CacheableEntity
    readonly expiresAt: Timestamp
}

interface EntityCacheDB extends DBSchema {
    [STORE]: {
        key: string
        value: PersistedOptimisticUpdate
        indexes: {
            [EXPIRES_INDEX]: number
        }
    }
}

// ── Pure TTL logic (unit-tested) ───────────────────────────────────

/**
 * Absolute expiry (epoch ms) for an overlay written at `now` with a `time`-second
 * TTL, clamped to MAX_OPTIMISTIC_UPDATE_TIME_SECONDS. The clamp bounds how long a
 * stale local overlay can mask fresh server data — a caller passing a huge `time`
 * (or a clock jump) can't pin an overlay in place indefinitely.
 */
export function optimisticExpiresAt(now: Timestamp, timeSeconds: number): Timestamp {
    return now + Math.min(timeSeconds, MAX_OPTIMISTIC_UPDATE_TIME_SECONDS) * 1000
}

/** An overlay is stale once `now` reaches its expiry; stale overlays are ignored on read and pruned. */
export function isOptimisticUpdateExpired(
    record: PersistedOptimisticUpdate,
    now: Timestamp
): boolean {
    return now >= record.expiresAt
}

// ── IDB glue ───────────────────────────────────────────────────────

let dbPromise: Promise<IDBPDatabase<EntityCacheDB>> | null = null

/**
 * Lazily-opened, module-singleton connection — or null under SSR / node tests
 * where `indexedDB` is absent. A null db means "no persistence": the in-memory
 * cache still works, only cross-reload survival is skipped.
 */
function getDb(): Promise<IDBPDatabase<EntityCacheDB>> | null {
    if (typeof indexedDB === 'undefined') return null
    if (!dbPromise) {
        dbPromise = openDB<EntityCacheDB>(DB_NAME, DB_VERSION, {
            upgrade(db) {
                const store = db.createObjectStore(STORE, { keyPath: 'key' })
                store.createIndex(EXPIRES_INDEX, 'expiresAt')
            },
        })
    }
    return dbPromise
}

/**
 * Temporarily assume an entity will successfully save on the server: apply it to
 * the in-memory cache now so every `liveEntity` subscriber updates in the same
 * tick, and (when `persist`) mirror it to IndexedDB so the overlay survives a
 * reload until its TTL lapses. The in-memory apply is synchronous and never waits
 * on the persistence round-trip.
 */
export function optimisticUpdate(entity: CacheableEntity, options?: OptimisticUpdateOptions): void {
    const key = entityCacheKey(entity)
    if (!key) return
    const { time, persist } = { ...optimisticUpdateDefaults, ...options }
    const expiresAt = optimisticExpiresAt(Date.now(), time)
    // Register before the in-memory apply so this overlay is authoritative from
    // the first write and a fresh call supersedes any prior overlay for the key.
    registerActiveOverlay(key, entity, expiresAt)
    updateEntity(entity)
    if (!persist) return
    const db = getDb()
    if (!db) return
    // Best-effort: a failed overlay write must not undo the optimistic UI, so the
    // promise is fire-and-forget with its rejection logged rather than surfaced.
    db.then((d) => d.put(STORE, { key, entity, expiresAt })).catch((e) => logError(target, e))
}

/**
 * Restore `key`'s persisted overlay (if any, non-expired) into the in-memory
 * registry and re-apply it — bridging a reload, where `activeOverlays` starts
 * empty but idb still holds the overlay. Best-effort; a read failure is logged,
 * not surfaced.
 */
function checkOptimisticUpdate(key: string): void {
    getOptimisticUpdate(key)
        .then((record) => {
            if (!record) return
            // Re-arm the in-memory registry (empty on a fresh load) with the
            // persisted TTL so the restored overlay stays authoritative over
            // later server writes — an SWR revalidation after restore can't
            // re-clobber it — until its original deadline.
            registerActiveOverlay(key, record.entity, record.expiresAt)
            updateEntity(record.entity)
        })
        .catch((error) => {
            logError(target, error)
        })
}

/**
 * The persisted overlay record for `key`, or null when there is none, it has
 * expired, or persistence is unavailable. An expired overlay resolves null (and
 * is not returned) so a reload never re-applies an overlay past its TTL. Reads
 * are best-effort: any idb failure resolves null rather than rejecting.
 */
async function getOptimisticUpdate(key: string): Promise<PersistedOptimisticUpdate | null> {
    const db = getDb()
    if (!db) return null
    try {
        const record = await (await db).get(STORE, key)
        if (!record || isOptimisticUpdateExpired(record, Date.now())) return null
        return record
    } catch (e) {
        logError(target, e)
        return null
    }
}

/**
 * Delete every persisted overlay that has passed its `expiresAt`. Walks the
 * `expiresAt` index bounded by the current time, so it touches only the stale
 * tail rather than scanning the store. Best-effort: resolves even when a delete
 * fails partway, logging the error.
 */
export async function pruneOptimisticUpdates(): Promise<void> {
    const db = getDb()
    if (!db) return
    try {
        const tx = (await db).transaction(STORE, 'readwrite')
        const index = tx.store.index(EXPIRES_INDEX)
        // Inclusive bound: the same `now >= expiresAt` staleness rule getOptimisticUpdate uses.
        let cursor = await index.openCursor(IDBKeyRange.upperBound(Date.now()))
        while (cursor) {
            await cursor.delete()
            cursor = await cursor.continue()
        }
        await tx.done
    } catch (e) {
        logError(target, e)
    }
}
